import React from 'react';
import { Link } from 'react-router-dom';
import { Building2, Users, User, Check, ArrowRight, Target, Shield, TrendingUp } from 'lucide-react';
import UnifiedHeader from '../components/UnifiedHeader';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

const AboutPage = () => {
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-rose-50 to-orange-50 flex flex-col">
      <UnifiedHeader />

      {/* Main Content with top padding for fixed nav */}
      <div className="pt-16 flex-1">
        {/* Hero */}
        <section className="pt-12 pb-16 px-6">
          <div className="container mx-auto max-w-4xl text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-6">
              Revolutionizing How We <span className="bg-gradient-to-r from-rose-400 to-orange-400 bg-clip-text text-transparent">Try On Clothes</span>
            </h1>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              VirtualFit uses cutting-edge AI to let you see exactly how clothes will look on you before you buy. We're helping brands reduce returns, influencers create stunning content, and shoppers make confident purchases.
            </p>
          </div>
        </section>

        {/* Mission */}
        <section className="py-16 px-6 bg-white/50">
          <div className="container mx-auto max-w-5xl">
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                <div className="w-14 h-14 bg-rose-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Target className="w-7 h-7 text-rose-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Our Mission</h3>
                <p className="text-gray-600">To eliminate the uncertainty of online shopping by letting you virtually try before you buy.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                <div className="w-14 h-14 bg-rose-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-7 h-7 text-rose-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Privacy First</h3>
                <p className="text-gray-600">Your photos are processed securely and never shared. We take your privacy seriously.</p>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-sm text-center">
                <div className="w-14 h-14 bg-rose-100 rounded-xl flex items-center justify-center mx-auto mb-4">
                  <TrendingUp className="w-7 h-7 text-rose-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">Real Impact</h3>
                <p className="text-gray-600">Our technology reduces fashion returns by up to 50%, saving money and the environment.</p>
              </div>
            </div>
          </div>
        </section>

        {/* Who We Serve */}
        <section className="py-16 px-6">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-3xl font-bold text-gray-800 text-center mb-12">Who We Serve</h2>
            
            <div className="grid md:grid-cols-3 gap-8">
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-6">
                  <Building2 className="w-7 h-7 text-blue-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Fashion Brands</h3>
                <p className="text-gray-600 mb-4">
                  Reduce return rates by up to 50% and save on shipping costs. Let customers see exactly how your products look before buying.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />Unlimited product portfolio</li>
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />ROI analytics dashboard</li>
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />Influencer collaborations</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-6">
                  <Users className="w-7 h-7 text-purple-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Influencers</h3>
                <p className="text-gray-600 mb-4">
                  Create stunning virtual outfit content and earn through brand campaigns. Build your personal brand on VirtualFit.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />Personal profile page</li>
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />Affiliate link integration</li>
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />Brand collaboration access</li>
                </ul>
              </div>
              
              <div className="bg-white rounded-2xl p-8 shadow-lg">
                <div className="w-14 h-14 bg-rose-100 rounded-xl flex items-center justify-center mb-6">
                  <User className="w-7 h-7 text-rose-500" />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-3">Shoppers</h3>
                <p className="text-gray-600 mb-4">
                  Never buy the wrong size again. See exactly how clothes will look on you before adding to cart. Try on anything, from anywhere.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />Try before you buy</li>
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />Download & share looks</li>
                  <li className="flex items-center"><Check className="w-4 h-4 text-green-500 mr-2" />Works with any online store</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Technology */}
        <section className="py-16 px-6 bg-white/50">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold text-gray-800 mb-6">Powered by Advanced AI</h2>
            <p className="text-lg text-gray-600 mb-8">
              VirtualFit uses Google's Gemini AI to generate photorealistic try-on images. Our technology preserves your exact appearance while seamlessly blending clothing onto your photo.
            </p>
            <div className="grid grid-cols-3 gap-6 text-center">
              <div>
                <p className="text-3xl font-bold text-rose-500">2 sec</p>
                <p className="text-gray-500">Average generation time</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-rose-500">95%</p>
                <p className="text-gray-500">Face preservation accuracy</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-rose-500">HD</p>
                <p className="text-gray-500">Image quality output</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 px-6">
          <div className="container mx-auto max-w-3xl">
            <div className="bg-gradient-to-r from-rose-400 to-orange-400 rounded-3xl p-12 text-center text-white">
              <h2 className="text-3xl font-bold mb-4">Ready to Get Started?</h2>
              <p className="text-lg opacity-90 mb-8">Join VirtualFit today and transform how you shop, sell, or showcase fashion.</p>
              <Link
                to={isAuthenticated ? "/dashboard" : "/subscribe"}
                className="inline-flex items-center px-8 py-4 bg-white text-rose-500 rounded-xl font-semibold text-lg hover:shadow-xl transition-all"
              >
                {isAuthenticated ? "Go to Dashboard" : "Subscribe Now"} <ArrowRight className="ml-2 w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>
      </div>

      <Footer isAuthenticated={isAuthenticated} />
    </div>
  );
};

export default AboutPage;
